import { Application, Container, Graphics } from 'pixi.js';
import { icon } from './icons.ts';
import { mapById, type TdMap } from '@thrash/sim';
import { loadSave } from './progress.ts';
import { Camera } from './camera.ts';
import { DefenseMode } from './defense.ts';
import { openHelp } from './help.ts';
import { createMenu } from './menu.ts';
import { closeScreens } from './hud.ts';
import { openCredits } from './credits.ts';
import { GAME_NAME } from './brand.ts';
import { openSettings } from './settings.ts';
import { hideTag } from './tag.ts';
import { hideConsole } from './console.ts';
import { hideFeed } from './feed.ts';
import { drawOutskirts } from './render.ts';
import { is3D } from './view3d.ts';
import { Scene3D } from './scene3d.ts';
import { setViewAngle, viewAngle } from './iso.ts';
import { bindKey, ignoreKey, pressShortcut } from './shortcuts.ts';
import { perfEnabled, setPerfEnabled, startPerfReadout } from './perf.ts';
import { unlockAudio } from './sound.ts';
import { isPanKey, panStep } from './keys.ts';

document.title = GAME_NAME;
// the first click anywhere is the gesture that lets the browser play sound
document.addEventListener('pointerdown', () => unlockAudio(), { once: true, capture: true });
document.addEventListener('keydown', () => unlockAudio(), { once: true, capture: true });

const app = new Application();
// in the 3D look the Pixi canvas is transparent so the 3D scene underneath shows through
await app.init({ background: 0x10140e, backgroundAlpha: is3D() ? 0 : 1, resizeTo: window, antialias: true, resolution: Math.min(devicePixelRatio, 2), autoDensity: true });
const scene3d = is3D() ? new Scene3D() : null;
if (scene3d) document.body.appendChild(scene3d.canvas);
document.body.appendChild(app.canvas);
app.canvas.style.position = 'relative';
app.canvas.style.zIndex = '1';
app.canvas.addEventListener('contextmenu', e => e.preventDefault());
app.canvas.addEventListener('mousedown', e => { if (e.button === 1) e.preventDefault(); });

const world = new Container();
app.stage.addChild(world);
// the countryside around the map in the 2D look: drawn once and kept as one texture
const outskirts = new Graphics();

// ---- the orbiting camera: hold Control and drag to turn the view all the way round ----
const VIEW_KEY = 'inbound.view.v1';
const ORBIT_RATE = (Math.PI * 2) / 800;
function applyView(a: number): void {
  setViewAngle(a);
  outskirts.cacheAsTexture(false);
  if (!is3D()) drawOutskirts(outskirts);
  outskirts.cacheAsTexture({ resolution: 0.75 });
  app.canvas.dataset.viewAngle = String(((Math.round(viewAngle() * 1800 / Math.PI) / 10) % 360));
}
let savedView = 0;
try { savedView = Number(localStorage.getItem(VIEW_KEY)) || 0; } catch { /* storage unavailable */ }
applyView(savedView);
const camera = new Camera(app, world);
const hud = new Graphics();
app.stage.addChild(hud);
const panel = document.getElementById('panel')!;

const game = new DefenseMode(camera, panel, hud, {
  onHome: () => { game.setPaused(true); menu.show(); },
});
world.addChild(outskirts, game.root);
// in development the game is on the window, so a late wave or a nuclear launch can be forced from the console
if (import.meta.env.DEV) (window as unknown as { game: DefenseMode }).game = game;

function play(map: TdMap): void {
  closeScreens();
  hideTag(); hideConsole(); hideFeed();
  game.start(map);
  camera.fit();
}

const menu = createMenu({
  play,
  inPlay: () => game.finished ? null : game.mapInPlay,
  resume: () => { game.setPaused(false); game.renderPanel(); },
  saved: () => { const s = loadSave(); const map = s ? mapById(s.mapId) : undefined; return s && map ? { map, wave: s.wave } : null; },
  continueSaved: () => {
    const s = loadSave();
    const map = s ? mapById(s.mapId) : undefined;
    if (!s || !map) return;
    closeScreens();
    hideTag(); hideConsole(); hideFeed();
    game.restore(map, s.seed, s.log, s.tick);
    camera.fit();
  },
  settings: anchor => openSettings(anchor, { onPerf: applyPerf, onDev: () => menu.refresh() }),
  help: () => openHelp(),
  credits: () => openCredits(),
}, import.meta.env.VITE_BUILD as string | undefined);

if (import.meta.env.DEV) Object.assign(window, { __game: game, __scene3d: scene3d });

for (const [id, name] of [['home-btn', 'home'], ['settings-btn', 'settings'], ['help-btn', 'help'], ['fullscreen-btn', 'fullscreen']] as const) document.getElementById(id)!.replaceChildren(icon(name));
const homeBtn = document.getElementById('home-btn') as HTMLButtonElement;
homeBtn.addEventListener('click', () => { game.setPaused(true); menu.show(); });
bindKey(homeBtn, 'H');
menu.show();
camera.fit();
requestAnimationFrame(() => { const idle = (window as Window & { requestIdleCallback?: (f: () => void) => void }).requestIdleCallback; (idle ?? ((f: () => void) => setTimeout(f, 0)))(() => scene3d?.finishInit()); });
document.getElementById('help-btn')!.addEventListener('click', () => openHelp());
const settingsBtn = document.getElementById('settings-btn')!;
settingsBtn.addEventListener('click', () => openSettings(settingsBtn, { onPerf: applyPerf, onDev: () => menu.refresh() }));
window.addEventListener('resize', () => camera.fit());

const canvas = app.canvas;
const local = (e: PointerEvent): [number, number] => {
  const p = world.toLocal({ x: e.clientX, y: e.clientY });
  return [p.x, p.y];
};
// Gestures. One finger: the game gets down/move/up, plus tap() if it barely moved.
// Two fingers: pinch to zoom and drag to pan, and the one-finger gesture is cancelled.
const TAP_SLOP = 8;
const pointers = new Map<number, { x: number; y: number }>();
let gesture: { id: number; x: number; y: number; moved: boolean; button: number } | null = null;
let pinch: { d: number; mx: number; my: number } | null = null;
const twoFinger = () => {
  const [a, b] = [...pointers.values()];
  return { d: Math.hypot(a!.x - b!.x, a!.y - b!.y) || 1, mx: (a!.x + b!.x) / 2, my: (a!.y + b!.y) / 2 };
};

let orbit: { id: number; x: number; a0: number; moved: boolean } | null = null;
let pendingView: number | null = null;
let viewReturn: { from: number; delta: number; start: number } | null = null;
const RETURN_MS = 250;

function saveView(): void {
  try { localStorage.setItem(VIEW_KEY, String(viewAngle())); } catch { /* ignore */ }
}

function returnView(): void {
  const from = viewAngle();
  const delta = ((-from + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI;
  if (Math.abs(delta) < 1e-6) return;
  viewReturn = { from, delta, start: performance.now() };
}

canvas.addEventListener('pointerdown', e => {
  if (e.button === 0 && e.ctrlKey && e.pointerType === 'mouse') {
    if (orbit || gesture) return;
    canvas.setPointerCapture(e.pointerId);
    viewReturn = null;
    orbit = { id: e.pointerId, x: e.clientX, a0: viewAngle(), moved: false };
    return;
  }
  canvas.setPointerCapture(e.pointerId);
  pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (pointers.size === 2) {
    game.cancelGesture();
    gesture = null;
    pinch = twoFinger();
    return;
  }
  if (pointers.size > 2 || pinch) return;
  gesture = { id: e.pointerId, x: e.clientX, y: e.clientY, moved: false, button: e.button };
  const [wx, wy] = local(e);
  game.pointerDown(wx, wy, e.clientX, e.clientY, e.button);
});
canvas.addEventListener('pointermove', e => {
  if (orbit && e.pointerId === orbit.id) {
    const dx = e.clientX - orbit.x;
    if (Math.abs(dx) > TAP_SLOP) orbit.moved = true;
    if (orbit.moved) pendingView = orbit.a0 + dx * ORBIT_RATE;
    return;
  }
  if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (pinch && pointers.size >= 2) {
    const now = twoFinger();
    camera.zoomAt(now.mx, now.my, now.d / pinch.d);
    camera.panBy(now.mx - pinch.mx, now.my - pinch.my);
    pinch = now;
    return;
  }
  const [wx, wy] = local(e);
  if (gesture && e.pointerId === gesture.id && Math.hypot(e.clientX - gesture.x, e.clientY - gesture.y) > TAP_SLOP) gesture.moved = true;
  if (!pinch) game.pointerMove(wx, wy, e.clientX, e.clientY);
});
const release = (e: PointerEvent, cancelled: boolean) => {
  if (orbit && e.pointerId === orbit.id) {
    const o = orbit;
    orbit = null;
    if (o.moved) {
      if (pendingView !== null) { applyView(pendingView); pendingView = null; }
      game.onViewChange();
      saveView();
    } else if (!cancelled) returnView();
    return;
  }
  pointers.delete(e.pointerId);
  if (pinch) { if (pointers.size === 0) pinch = null; return; }
  if (!gesture || e.pointerId !== gesture.id) return;
  const g = gesture;
  gesture = null;
  game.pointerUp();
  if (!cancelled && !g.moved) {
    const [wx, wy] = local(e);
    if (g.button === 2) { game.rightClick(wx, wy); return; }
    game.tap(wx, wy, g.button, e.timeStamp);
  }
};
canvas.addEventListener('pointerup', e => release(e, false));
canvas.addEventListener('pointercancel', e => release(e, true));
window.addEventListener('keydown', e => {
  if (isPanKey(e)) { e.preventDefault(); return; }
  if (ignoreKey(e)) return;
  if (e.key === 'F3' && e.shiftKey) { setPerfEnabled(!perfEnabled()); applyPerf(); e.preventDefault(); return; }
  if (pressShortcut(e.key)) { e.preventDefault(); return; }
  game.key(e.key);
});

// full screen: offered wherever the browser allows it
const fullBtn = document.getElementById('fullscreen-btn') as HTMLButtonElement;
const showFull = () => {
  const on = document.fullscreenElement !== null;
  const label = on ? 'Exit full screen' : 'Full screen';
  fullBtn.setAttribute('aria-label', label);
  fullBtn.title = `${label} · Shortcut: F11`;
  fullBtn.classList.toggle('active', on);
};
if (document.fullscreenEnabled) {
  fullBtn.hidden = false;
  fullBtn.addEventListener('click', () => {
    const go = document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen({ navigationUI: 'hide' });
    void go.catch(() => {});
  });
  document.addEventListener('fullscreenchange', showFull);
  showFull();
}
bindKey(document.getElementById('help-btn')!, '?');

if (scene3d) app.ticker.add(() => scene3d.sync(app, world));
const perfEl = document.getElementById('perf')!;
let perfStarted = false;
function applyPerf(): void {
  const on = perfEnabled();
  perfEl.hidden = !on;
  if (on && !perfStarted) { perfStarted = true; startPerfReadout(app, perfEl, scene3d?.gpuTimer ?? null); }
}
applyPerf();

app.ticker.add(ticker => {
  const noHome = !game.active;
  if (homeBtn.hidden !== noHome) homeBtn.hidden = noHome;
  if (viewReturn) {
    const t = Math.min(1, (performance.now() - viewReturn.start) / RETURN_MS);
    const eased = 1 - (1 - t) ** 3;
    pendingView = t >= 1 ? 0 : viewReturn.from + viewReturn.delta * eased;
    if (t >= 1) {
      viewReturn = null;
      applyView(0);
      pendingView = null;
      game.onViewChange();
      saveView();
    }
  }
  const [px, py] = panStep(ticker.deltaMS);
  if (px !== 0 || py !== 0) camera.panBy(px, py);
  if (pendingView !== null) {
    applyView(pendingView);
    pendingView = null;
    game.onViewChange();
  }
  game.update(ticker.deltaMS);
});
